import Vue from 'vue';
import VueRouter from 'vue-router';

import { safeNextPath } from '~/util/safePath';

const Home = () => import('./views/Home.vue');

// Activity views for desktop
const Activity = () => import('./views/activity/Activity.vue');
const ActivityView = () => import('./views/activity/ActivityView.vue');

const Buckets = () => import('./views/Buckets.vue');
const Bucket = () => import('./views/Bucket.vue');
const QueryExplorer = () => import('./views/QueryExplorer.vue');
const Trends = () => import('./views/Trends.vue');
const Settings = () => import('./views/settings/Settings.vue');
// CategoryBuilder is no longer a top-level route — it's embedded inside
// CategorizationSettings. The /settings/category-builder path is now a
// redirect, so no direct reference here. Keeping the import out avoids
// pulling a second copy into a separate chunk.
const Stopwatch = () => import('./views/Stopwatch.vue');
const WorkReport = () => import('./views/WorkReport.vue');
const RecurringWork = () => import('./views/RecurringWork.vue');
const LocalWorkAnalysis = () => import('./views/LocalWorkAnalysis.vue');
const OrganizationDiagnostic = () => import('./views/OrganizationDiagnostic.vue');
const AdminConsole = () => import('./views/AdminConsole.vue');
const Devices = () => import('./views/Devices.vue');
const DeviceActivity = () => import('./views/DeviceActivity.vue');
const DeviceTimeline = () => import('./views/DeviceTimeline.vue');
const DeviceDiagnostics = () => import('./views/DeviceDiagnostics.vue');
const BillingView = () => import('./views/BillingView.vue');
const AISummaryView = () => import('./views/AISummaryView.vue');
const Alerts = () => import('./views/Alerts.vue');
const Search = () => import('./views/Search.vue');
const Report = () => import('./views/Report.vue');
const TimespiralView = () => import('./views/TimespiralView.vue');
const Dev = () => import('./views/Dev.vue');
const Graph = () => import('./views/Graph.vue');
const NotFound = () => import('./views/NotFound.vue');
const SignIn = () => import('./views/auth/SignIn.vue');
const Setup = () => import('./views/auth/Setup.vue');
const AcceptInvite = () => import('./views/auth/AcceptInvite.vue');
const Account = () => import('./views/Account.vue');
const Firm = () => import('./views/Firm.vue');
const Zunox = () => import('./views/Zunox.vue');

Vue.use(VueRouter);

const router = new VueRouter({
  // The local server returns the dashboard shell for non-API routes, so use
  // clean history URLs instead of hash-based navigation.
  mode: 'history',
  routes: [
    {
      path: '/',
      redirect: _to => {
        return localStorage.landingpage || '/home';
      },
    },
    { path: '/home', component: Home },
    // Reachable while signed out (see App.vue): the first admin's setup and
    // choosing a password from an invite or reset link.
    { path: '/login', component: SignIn, meta: { public: true } },
    { path: '/setup', component: Setup, meta: { public: true } },
    { path: '/invite', component: AcceptInvite, meta: { public: true } },
    { path: '/account', component: Account },
    { path: '/firm', component: Firm },
    // A client's mornings, check sheets and readouts, through the engine service.
    { path: '/zunox', component: Zunox },
    {
      path: '/activity/:host/:periodLength?/:date?',
      component: Activity,
      props: true,
      children: [
        {
          path: 'view/:view_id?',
          meta: { subview: 'view' },
          name: 'activity-view',
          component: ActivityView,
          props: true,
        },
        // Unspecified should redirect to summary view is the summary view
        // (needs to be last since otherwise it'll always match first)
        {
          path: '',
          redirect: 'view/',
        },
      ],
    },
    { path: '/buckets', component: Buckets },
    { path: '/buckets/:id', component: Bucket, props: true },
    // The local-bucket timeline has no data now that tracking happens on
    // paired devices; send old bookmarks and saved landing pages to the
    // device timeline instead.
    { path: '/timeline', redirect: '/devices/timeline' },
    { path: '/trends', component: Trends, meta: { fullContainer: true } },
    { path: '/trends/:host', component: Trends, meta: { fullContainer: true } },
    { path: '/report', component: Report },
    { path: '/query', component: QueryExplorer },
    { path: '/alerts', component: Alerts },
    { path: '/timespiral', component: TimespiralView },
    { path: '/settings', component: Settings },
    // Category Builder now lives embedded inside the Categorization
    // settings panel; keep the old standalone route as a redirect so
    // external links / bookmarks still land somewhere useful.
    { path: '/settings/category-builder', redirect: '/settings/categorization' },
    // :group lets the active settings panel survive reloads / be linkable.
    // The matcher excludes 'category-builder' so the more specific route above
    // wins; new groups added in Settings.vue should also be added here.
    {
      path: '/settings/:group(general|appearance|categorization|privacy|developer|notifications)',
      component: Settings,
      props: true,
    },
    { path: '/stopwatch', component: Stopwatch },
    { path: '/work-report', component: WorkReport },
    { path: '/recurring-work', component: RecurringWork },
    { path: '/work-analysis', component: LocalWorkAnalysis },
    { path: '/organization-diagnostic', component: OrganizationDiagnostic },
    { path: '/admin', component: AdminConsole },
    { path: '/devices', component: Devices },
    { path: '/devices/diagnostics/:deviceId', component: DeviceDiagnostics, props: true },
    {
      path: '/devices/activity/:deviceId?',
      component: DeviceActivity,
      props: route => ({ deviceId: route.params.deviceId, date: route.query.date }),
    },
    {
      path: '/devices/timeline/:deviceId?',
      component: DeviceTimeline,
      props: true,
      meta: { fullContainer: true },
    },
    { path: '/billing', component: BillingView },
    { path: '/analysis/activity', component: AISummaryView },
    { path: '/search', component: Search },
    { path: '/graph', component: Graph },
    { path: '/dev', component: Dev },
    // NOTE: Will break with Vue 3: https://stackoverflow.com/questions/40193634/vue-router-redirect-on-page-not-found-404/64186073#64186073
    {
      path: '*',
      component: NotFound,
    },
  ],
});

/**
 * Each page's code is a separate file whose name changes with every build.
 * A tab opened before the dashboard was rebuilt still asks for the old
 * names, which are gone, so navigating would silently do nothing. Load the
 * page fresh instead, once, so the tab picks up the current build.
 */
export function isStaleBuildError(err) {
  const text = String((err && (err.name || '')) + ' ' + ((err && err.message) || ''));
  return /ChunkLoadError|Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module/i.test(
    text
  );
}

const RELOADED_FOR = 'zunox-reloaded-for-build';

// Vue Router 3 doesn't say which navigation failed, so remember the last
// one started: that's where the reload should land.
let pendingPath = null;
router.beforeEach((to, _from, next) => {
  pendingPath = safeNextPath(to.fullPath);
  next();
});

router.onError(err => {
  if (!isStaleBuildError(err)) return;
  const target = pendingPath || window.location.pathname;
  try {
    // Never loop: one reload per target per tab.
    if (sessionStorage.getItem(RELOADED_FOR) === target) return;
    sessionStorage.setItem(RELOADED_FOR, target);
  } catch {
    // Without storage, still reload once; a second failure lands here again.
  }
  window.location.assign(target);
});

export default router;
